import type { NpcConfig } from '../config/npc';
import type { FoliageStroke } from '../foliageStrokes';
import type { WorldBounds } from '../utils/worldBounds';
import { parseSpeaker, type SpeakerConfig, type WorldMusicSettings } from '../services/music/musicTypes';
import { parseWorldGame, type WorldGameConfig } from '../components/games/worldgame/worldGameTypes';
import { create } from 'zustand';
import { temporal } from 'zundo';
import { supabase } from '../config/supabase';
import { getStoredUserName } from '../utils/nameGenerator';
import { isHiddenUser } from './safetyStore';

// ---- Types ----

export type CameraMode = 'normal' | 'zoom' | 'birdseye';

export interface EnvironmentModel {
  url: string;
  transform: {
    position: [number, number, number];
    scale: number;
    rotation: [number, number, number];
    isLocked: boolean;
  };
  visible: boolean;
  // 'auto' (default) tags every solid mesh as collidable. 'none' turns the
  // model into scenery: use it when the GLB bakes a backdrop (a city, a
  // terrain skirt) into the same file as the playable set, where auto-tagging
  // would make the backdrop's bounding box the floor the player falls to.
  collision?: 'auto' | 'none';
}

export interface SkyboxEntry {
  url: string;
  filename: string;
  uploadedAt: number;
  thumbnail?: string;
}

export interface ContentItem {
  url: string;
  type: 'image' | 'audio' | 'model' | 'splat' | 'ply';
  position: [number, number, number];
  scale: number;
  rotation: [number, number, number];
  visible: boolean;
  isLocked?: boolean;
  collidable?: boolean; // meshes tagged for avatar collision (VR + first-person)
  filename?: string;
  thumbnailUrl?: string;
  uploadedAt?: number;
  // Set on freshly-added library/upload models so ContentDisplay can measure
  // the loaded geometry once and normalize scale to a human-usable size,
  // then clears itself. Not persisted meaningfully (cleared after fit).
  autoFit?: boolean;
  // Marked as a place to sit — players can click it in the world to seat their
  // avatar (snap + sit pose). Persisted in the asset metadata.
  seat?: boolean;
  // Texture-reduced copy for GPUs that cap below the main file's textures
  // (Quest/phones stop at 4096px, where an oversized texture fails to upload
  // and the model renders untextured). Chosen at load from the real GPU limit.
  mobileUrl?: string;
  // Render this model as the sky: unlit, visible from the inside, no shadows,
  // no collision, auto-sized to enclose the world. Blockade's mesh export is a
  // panorama on a depth-displaced mesh, so unlike a flat skybox it gives real
  // parallax as you move.
  environmentMesh?: boolean;
  // Grouping — shared with primitives so mixed groups (shapes + models) move
  // and duplicate as one unit.
  groupId?: string;
  // Per-material-slot colour overrides, keyed by the GLB's material name
  // (venues use shell/deck/cushion/frame/accent/...). Recolouring a slot
  // repaints every piece that shares it — change one seat, all seats change.
  materialColors?: Record<string, string>;
  // Per-slot PBR material from the texture library. Models are box-projected at
  // one UV unit per metre, so `scale` is tiles-per-metre and reads the same on
  // every surface. Opt-in per slot: textures are the expensive part.
  materialTextures?: Record<string, { maps: PbrMaps; scale?: number }>;
}

export interface ScreenPropItem {
  type: 'screen-share' | 'browser' | 'video';
  position: [number, number, number];
  scale: number;
  rotation: [number, number, number];
  visible: boolean;
  isLocked?: boolean;
  streamData?: MediaStream | null;
  browserUrl?: string;
  videoUrl?: string;
  filename?: string;
  uploadedAt?: number;
  // Room screen: a shared screen fitted onto a venue model's own screen
  // surface (Watch Room, Cinema Room). Sized in metres at scale 1, no frame,
  // and it shows the room's cloud browser / synced video (see RoomScreen.tsx).
  roomScreen?: boolean;
  width?: number;
  height?: number;
  name?: string;
}

export type PrimitiveShape = 'box' | 'sphere' | 'cylinder' | 'cone' | 'plane' | 'torus' | 'text' | 'portal' | 'gametable' | 'arcade' | 'leaderboard' | 'npc' | 'speaker' | 'worldgame';

/** shape === 'leaderboard': what an arcade scoreboard shows and how it looks (games/arcade/arcadeShared.ts DEFAULT_BOARD). */
export interface LeaderboardConfig {
  /** Arcade game id, or 'all' to rotate through every game. */
  game: string;
  period: 'all' | 'week' | 'today';
  /** This world's scores, or every world's. */
  scope: 'world' | 'global';
  title: string;
  /** 'auto' (the game's own colours) or a palette id (BOARD_THEMES). */
  theme: string;
  rows: 5 | 10;
  mount: 'wall' | 'stand';
}

// ---- Build Mode v2 ----
// 'none'  → classic select + transform-gizmo flow (default, nothing changes)
// 'place' → ghost preview follows the pointer, click to stamp, repeat
// 'wall'  → drag on the ground to draw a wall segment
// 'room'  → drag a rectangle to get 4 walls + a floor
// 'paint' → click a primitive to recolor it with the active swatch
// 'door' / 'window' → hover a wall, click to cut an opening (the wall is
// replaced by segments around the hole — plain primitives, so save/multiplayer/
// collision/undo all keep working)
// 'stairs' → drag on the ground to generate a rising run of steps (grouped)
// 'select' → marquee: drag a box to select everything inside, drag inside the
// selection to move it all together, G groups it
// 'box' → direct modelling: drag a footprint on the grid, then pull it up to
// set the height. One continuous gesture instead of stamp-then-scale-3-axes.
export type BuildTool = 'none' | 'place' | 'wall' | 'room' | 'paint' | 'door' | 'window' | 'arch' | 'arch-wide' | 'stairs' | 'box' | 'select' | 'foliage';

// What the placement ghost is holding.
export interface PlacementSpec {
  kind: 'primitive' | 'content' | 'prefab' | 'generated' | 'venue';
  // kind === 'venue' — GLB shell plus its sit markers (see commitVenue)
  venueId?: string;
  // kind === 'prefab' — id into PREFABS (spawns a group of primitives)
  prefabId?: string;
  // kind === 'generated' — pre-generated parts (city buildings). The ghost
  // shows exactly these parts; after stamping, a fresh variation is generated.
  buildingKind?: string;
  parts?: any[];
  // kind === 'primitive'
  shape?: PrimitiveShape;
  // shape === 'portal': set when placing the EXIT of an existing portal
  portalExitFor?: string;
  // shape === 'gametable': which game (registry kind, e.g. 'spades')
  game?: string;
  // shape === 'arcade': which arcade game the cabinet plays (arcade registry id)
  arcadeGame?: string;
  // shape === 'leaderboard': the board's settings (placed with these)
  board?: LeaderboardConfig;
  // shape === 'npc': the character being placed (src/config/npc.ts)
  npc?: NpcConfig;
  // kind === 'content'
  url?: string;
  contentType?: 'image' | 'audio' | 'model' | 'splat' | 'ply';
  filename?: string;
  label?: string;
  // Built-in props: pre-flag placed items (sit-enabled chairs, solid furniture).
  // autoFit=false means the GLB is authored in real meters — don't rescale it.
  seat?: boolean;
  collidable?: boolean;
  autoFit?: boolean;
}

export interface PbrMaps {
  colorUrl: string;
  normalUrl?: string;
  roughnessUrl?: string;
  aoUrl?: string;
  metallicUrl?: string;
}

export interface PrimitiveItem {
  shape: PrimitiveShape;
  name: string;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  color: string;
  texturePreset: string | null;
  textureUrl: string | null;
  pbrMaps: PbrMaps | null;
  textureScale: number;
  // Optional separate vertical tiling — fixes textures looking squashed on
  // stretched shapes (walls etc). Absent = same as textureScale.
  textureScaleY?: number;
  visible: boolean;
  isLocked: boolean;
  collidable: boolean;
  // Glass / transparency
  opacity: number;        // 0–1, default 1 (fully opaque)
  metalness: number;      // 0–1, default 0
  roughness: number;      // 0–1, default 0.5
  // Neon glow: the shape emits its own color as light (emissiveIntensity).
  // 0 = off. With Atmosphere→Bloom enabled the glow gets a real halo.
  emissive?: number;      // 0–5, default 0
  // Click-to-sit: avatars can sit on this piece (used by prefab chair seats)
  seat?: boolean;
  // 'chair': sit height fitted to the avatar (hips just above the cushion)
  // instead of the fixed drop — for invisible markers over modelled chairs.
  // 'deep': fitted too, and the legs posed for the seat — hips resting on
  // the cushion, thighs level, calves over the front (avatar/chairSit.ts).
  // The marker box is the seat: its top the cushion, its front face the
  // chair's furthest-forward point (e.g. the conference rooms' tub pods).
  seatFit?: 'chair' | 'deep';
  // shape === 'portal': the two ends of a portal share a link id; the label
  // floats over the ring (e.g. where it leads).
  portalLink?: string;
  portalLabel?: string;
  // A portal can lead to another world instead: stepping in loads it. You
  // arrive beside that world's portal leading back here, if it has one.
  portalWorldId?: string;
  portalWorldName?: string;
  // shape === 'gametable': which game the table plays (e.g. 'spades'). The
  // server keys the table's state by (world, this primitive's id).
  game?: string;
  // shape === 'gametable': felt / wood / cushion / metal colours (hex).
  gameStyle?: { felt?: string; wood?: string; cushion?: string; metal?: string };
  // shape === 'arcade': which arcade game the cabinet plays (arcade/registry.ts id, e.g. 'blocks').
  arcadeGame?: string;
  // shape === 'leaderboard': which game / period / scope it shows, title, theme, rows, mount.
  board?: LeaderboardConfig;
  // shape === 'npc': look, animation and build state of a world character.
  npc?: NpcConfig;
  // shape === 'speaker': positional music (tracks, volume, range, loop).
  speaker?: SpeakerConfig;
  // shape === 'worldgame': a world-game piece (start / collectible / hunter / goal / scare zone).
  worldGame?: WorldGameConfig;
  colliderOnly: boolean;  // true = invisible mesh, collision only
  // false = lets light through (a room's walls and ceiling, so the sun still
  // shades the furniture inside). Absent = casts shadows.
  castShadow?: boolean;
  // Text-specific fields (only used when shape === 'text')
  text?: string;
  fontSize?: number;
  // Grouping
  groupId?: string;
}

export type LightType = 'spotlight' | 'pointlight' | 'arealight';

export interface LightItem {
  type: LightType;
  name: string;
  position: [number, number, number];
  rotation: [number, number, number];
  color: string;
  intensity: number;
  distance: number;
  angle: number;       // spotlight cone angle (radians)
  penumbra: number;    // spotlight edge softness 0-1
  castShadow: boolean;
  visible: boolean;
  isLocked: boolean;
  // arealight specific
  width: number;
  height: number;
}

export type ParticleType = 'none' | 'rain' | 'snow' | 'dust' | 'fireflies' | 'embers' | 'bubbles';
export type FogType = 'none' | 'linear' | 'exponential';

export interface AtmosphereSettings {
  fog: FogType;
  fogColor: string;
  fogNear: number;
  fogFar: number;
  fogDensity: number;
  particles: ParticleType;
  particleCount: number;
  particleColor: string;
  bloom: boolean;
  bloomIntensity: number;
  bloomThreshold: number;
  ambientIntensity: number;
  sunIntensity: number;
  sunColor: string;
  // Sun casts real shadows: ceilings/walls block sunlight so interiors go dark.
  // Off by default — shadow maps cost GPU on Quest/mobile.
  sunShadows?: boolean;
  // Half-size (m) of the square the sun's shadows cover around the origin.
  // Smaller = crisper shadows (a 5 m room vs. the 60 m default).
  shadowRadius?: number;
  // Particles held to one area of the world (e.g. embers over a dance floor),
  // in world coordinates. Animated entirely on the GPU.
  areaParticles?: AreaParticles | null;
}

export interface AreaParticles {
  type: 'embers' | 'fireflies';
  count: number;
  color: string;
  min: [number, number, number];
  max: [number, number, number];
}

export interface GroundSettings {
  size: number;
  color: string;
  textureUrl: string | null;
  texturePreset: string | null; // e.g. 'grass' — loads PBR maps from /textures/{preset}/
  pbrMaps?: PbrMaps | null;     // custom PBR set from the material library (ambientCG)
  textureScale: number; // tiling repeat multiplier (1 = large pattern, 20 = tiny pattern)
  opacity: number;      // 0 = fully see-through, 1 = solid. The plane still exists
                        // at 0 so object placement and collision keep working.
  showGrid: boolean;
  isLocked: boolean;
}

export interface OnlinePlayer {
  id: string;
  userName: string;
  avatarUrl: string;
  userId?: string | null;
}

export interface KnockRequest {
  userId: string;
  userName: string;
  timestamp: number;
}

export interface SaveNotification {
  show: boolean;
  type: 'success' | 'error';
  message: string;
}

// ---- Store ----

interface WorldState {
  // UI panels
  showChat: boolean;
  showScreenShare: boolean;
  showEnvironmentControls: boolean;
  showObjectsList: boolean;
  showControlsPanel: boolean;
  showEnvironmentPicker: boolean;
  showSkyboxPicker: boolean;
  showContentPicker: boolean;
  showAvatarSelector: boolean;
  showGroundControls: boolean;
  showContentBrowser: boolean;
  showSkyboxManager: boolean;
  showScreenPropPicker: boolean;
  showBrowserPropPicker: boolean;
  showAuthPrompt: boolean;
  showTimeWarning: boolean;
  showVoiceChatPrompt: boolean;
  showShareModal: boolean;
  showAtmosphereControls: boolean;

  // Avatar
  avatarUrl: string | null;
  /** Base-colour override from the worn "look". Null wears the original. */
  avatarLookTextureUrl: string | null;
  avatarAnimations: Record<string, string>; // {idle, walking, running, jumping}
  activeAvatarId: string | null;
  avatarVisible: boolean;
  userName: string;
  cameraMode: CameraMode;

  // World data
  environmentModels: Record<string, EnvironmentModel>;
  skyboxes: Record<string, SkyboxEntry>;
  currentSkyboxId: string | null;
  mobileSkyboxUrl: string | null;
  contentItems: Record<string, ContentItem>;
  screenProps: Record<string, ScreenPropItem>;
  primitives: Record<string, PrimitiveItem>;
  lights: Record<string, LightItem>;
  groundVisible: boolean;
  groundSettings: GroundSettings;
  /** Painted foliage, stored as brush strokes rather than as individual plants. */
  foliageStrokes: FoliageStroke[];
  /** Brush settings for the foliage tool (not saved with the world). */
  foliageBrush: { radius: number; density: number; species: number[]; trees: boolean; erase: boolean };
  atmosphere: AtmosphereSettings;
  spawnPosition: [number, number, number];
  /** Walkable rectangle players can't leave (settings.bounds); null = anywhere. */
  worldBounds: WorldBounds | null;
  /** The world's soundtrack (settings.music); null = none. */
  worldMusic: WorldMusicSettings | null;
  setWorldMusic: (music: WorldMusicSettings | null) => void;

  // Selection
  selectedObjectId: string | null;
  selectedObjectIds: string[]; // multi-select for grouping
  isEditingEnvironment: boolean;

  // Auth & session
  user: any;
  isOwner: boolean;
  isPublic: boolean;
  accessCode: string | null;

  // Multiplayer debug
  multiplayerInfo: { shardIndex: number | null; playerCount: number; socketId: string | null; connected: boolean };
  setMultiplayerInfo: (info: Partial<{ shardIndex: number | null; playerCount: number; socketId: string | null; connected: boolean }>) => void;

  // Online players (for the users bar)
  onlinePlayers: OnlinePlayer[];
  setOnlinePlayers: (players: OnlinePlayer[]) => void;

  // Knock requests (for world locking)
  knockRequests: KnockRequest[];
  addKnockRequest: (req: KnockRequest) => void;
  removeKnockRequest: (userId: string) => void;

  // World lockdown — creator can lock everyone out
  worldLocked: boolean;
  setWorldLocked: (locked: boolean) => void;

  // VR build mode — performance optimizations active
  vrBuildMode: boolean;
  setVrBuildMode: (active: boolean) => void;

  // VR actively building — build menu open or placement tool engaged.
  // Drives the aggressive quality throttle (VRBuildQualityProfile);
  // restored to full quality the moment building stops.
  vrBuildActive: boolean;
  setVrBuildActive: (active: boolean) => void;

  // In-world emotes — ephemeral trigger for the LOCAL player's avatar.
  // The incrementing nonce lets the same emote be re-triggered. NOT persisted
  // or synced through the write-through object lists (broadcast is done by the
  // caller in WorldBuilder).
  localEmote: { name: string; nonce: number } | null;
  playLocalEmote: (name: string) => void;
  stopLocalEmote: () => void;

  // In-world building tools
  activeTool: 'select' | 'place' | 'move' | 'rotate' | 'scale' | 'delete';
  setActiveTool: (tool: 'select' | 'place' | 'move' | 'rotate' | 'scale' | 'delete') => void;
  placementShape: PrimitiveShape | LightType | null;
  vrPanelType: 'screen-share' | 'browser' | null;
  setVrPanelType: (type: 'screen-share' | 'browser' | null) => void;
  setPlacementShape: (shape: PrimitiveShape | LightType | null) => void;
  snapSettings: { grid: boolean; gridSize: number; rotation: boolean; rotationAngle: number };
  setSnapSettings: (settings: Partial<{ grid: boolean; gridSize: number; rotation: boolean; rotationAngle: number }>) => void;

  // ---- Build Mode v2 (Bloxburg-style placement / wall / room / paint tools) ----
  // Which "smooth build" tool is armed. 'none' = the classic select+gizmo flow,
  // so every legacy interaction is untouched while this is 'none'.
  buildTool: BuildTool;
  setBuildTool: (tool: BuildTool) => void;
  // What the ghost preview is currently holding (only meaningful for 'place')
  placementSpec: PlacementSpec | null;
  setPlacementSpec: (spec: PlacementSpec | null) => void;
  // Ghost yaw in radians — bumped by 90° with the R key while placing
  placementRotY: number;
  setPlacementRotY: (rot: number) => void;
  // Stairs curve sweep in degrees (0 = straight; R cycles 0/90/180/360 while
  // the stairs tool is armed). Curved: drag start = center, drag end = first step.
  stairSweep: number;
  setStairSweep: (deg: number) => void;
  // Overhead architect camera
  topDownView: boolean;
  setTopDownView: (on: boolean) => void;
  // Active swatch for paint mode
  paintColor: string;
  setPaintColor: (color: string) => void;

  // Edit locks — maps object ID → { userId, userName } of who is editing
  editLocks: Record<string, { userId: string; userName: string; at?: number }>;
  setEditLock: (objectId: string, lock: { userId: string; userName: string } | null) => void;
  lockFlash: { id: string; at: number } | null;
  flashLocked: (objectId: string) => void;

  // Scene object render order (drag reordering in objects panel)
  sceneOrder: string[];
  setSceneOrder: (order: string[]) => void;

  // Track objects received from other players (not shown in local scene list)
  remoteObjectIds: Set<string>;
  addRemoteObjectId: (id: string) => void;
  removeRemoteObjectId: (id: string) => void;

  // Role: 'owner' | 'host' | 'builder' | 'viewer' — determines editing permissions
  // host = promoted by owner, gets full edit access including settings
  role: 'owner' | 'host' | 'builder' | 'viewer';
  setRole: (role: 'owner' | 'host' | 'builder' | 'viewer') => void;
  // True when the loaded world's asset rows carry stable ids, enabling
  // per-object write-through (legacy worlds stay manual-save until re-saved)
  persistenceReady: boolean;
  setPersistenceReady: (ready: boolean) => void;
  // canBuild = owner, host, or builder
  canBuild: boolean;

  // Loading state
  worldLoading: boolean;
  loadingMessage: string;
  worldName: string;
  setWorldLoading: (loading: boolean) => void;
  setLoadingMessage: (msg: string) => void;
  setWorldName: (name: string) => void;

  // Save state
  isSaving: boolean;
  lastSaved: Date | null;
  actualWorldId: string | null;
  saveNotification: SaveNotification;
  shortenedURL: string;
  isLoadingShortURL: boolean;
  activeScreenShare: MediaStream | null;

  // Actions - UI toggles
  togglePanel: (panel: string) => void;
  setPanel: (panel: string, value: boolean) => void;

  // Actions - Avatar
  setAvatarUrl: (url: string | null) => void;
  setAvatarLookTextureUrl: (url: string | null) => void;
  setAvatarFromDb: (avatar: { id: string; model_url: string; animations: Record<string, string>; active_texture_url?: string | null }) => void;
  setUserName: (name: string) => void;
  setCameraMode: (mode: CameraMode) => void;

  // Actions - World data
  addEnvironmentModel: (id: string, model: EnvironmentModel) => void;
  updateEnvironmentModel: (id: string, updates: Partial<EnvironmentModel>) => void;
  updateEnvironmentTransform: (id: string, transform: Partial<EnvironmentModel['transform']>) => void;
  deleteEnvironmentModel: (id: string) => void;

  addSkybox: (id: string, skybox: SkyboxEntry) => void;
  deleteSkybox: (id: string) => void;
  setCurrentSkyboxId: (id: string | null) => void;
  setMobileSkyboxUrl: (url: string | null) => void;

  addContentItem: (id: string, item: ContentItem) => void;
  updateContentItem: (id: string, updates: Partial<ContentItem>) => void;
  deleteContentItem: (id: string) => void;

  addScreenProp: (id: string, prop: ScreenPropItem) => void;
  updateScreenProp: (id: string, updates: Partial<ScreenPropItem>) => void;
  deleteScreenProp: (id: string) => void;

  addPrimitive: (id: string, item: PrimitiveItem) => void;
  updatePrimitive: (id: string, updates: Partial<PrimitiveItem>) => void;
  deletePrimitive: (id: string) => void;

  addLight: (id: string, item: LightItem) => void;
  updateLight: (id: string, updates: Partial<LightItem>) => void;
  deleteLight: (id: string) => void;

  setGroundSettings: (settings: Partial<GroundSettings>) => void;
  addFoliageStroke: (stroke: FoliageStroke) => void;
  undoFoliageStroke: () => void;
  clearFoliageStrokes: () => void;
  setFoliageStrokes: (strokes: FoliageStroke[]) => void;
  setFoliageBrush: (b: Partial<{ radius: number; density: number; species: number[]; trees: boolean; erase: boolean }>) => void;
  setGroundVisible: (visible: boolean) => void;
  setAtmosphere: (settings: Partial<AtmosphereSettings>) => void;
  setSpawnPosition: (pos: [number, number, number]) => void;
  setWorldBounds: (bounds: WorldBounds | null) => void;

  // Actions - Selection
  setSelectedObjectId: (id: string | null) => void;
  toggleMultiSelect: (id: string) => void;
  setMultiSelect: (ids: string[]) => void;
  clearMultiSelect: () => void;
  setIsEditingEnvironment: (editing: boolean) => void;

  // Actions - Grouping
  groupSelected: () => void;
  ungroupSelected: () => void;
  getGroupMembers: (groupId: string) => string[];

  // Actions - Auth
  setUser: (user: any) => void;
  setIsOwner: (isOwner: boolean) => void;
  setIsPublic: (isPublic: boolean) => void;
  setAccessCode: (code: string | null) => void;
  generateAccessCode: () => string;

  // Actions - Save
  setIsSaving: (saving: boolean) => void;
  setLastSaved: (date: Date | null) => void;
  setActualWorldId: (id: string | null) => void;
  setSaveNotification: (notification: SaveNotification) => void;
  setActiveScreenShare: (stream: MediaStream | null) => void;
  setShortenedURL: (url: string) => void;
  setIsLoadingShortURL: (loading: boolean) => void;

  // Reset world-specific data (when entering a new world)
  resetWorldData: () => void;

  // Bulk loaders
  loadWorldAssets: (assets: any[]) => void;
  setSkyboxes: (skyboxes: Record<string, SkyboxEntry>) => void;
  setEnvironmentModels: (models: Record<string, EnvironmentModel>) => void;
  setContentItems: (items: Record<string, ContentItem>) => void;
}

// Default avatar — null means user must choose; fallback capsule renders in-world
const DEFAULT_AVATAR_URL: string | null = null;

// Fields that should be tracked for undo/redo (world data only, not UI,
// selection, auth or your own avatar). Undo/redo go through services/worldUndo.
export const TRACKED_FIELDS = new Set([
  'environmentModels', 'contentItems', 'screenProps', 'primitives', 'lights',
  'skyboxes', 'currentSkyboxId', 'groundVisible', 'groundSettings',
  'atmosphere', 'spawnPosition',
]);
const UNDO_MERGE_MS = 500;

export const useWorldStore = create<WorldState>()(temporal((set, get) => ({
  // UI panels
  showChat: false,
  showScreenShare: false,
  showEnvironmentControls: true,
  showObjectsList: false,
  showControlsPanel: true,
  showEnvironmentPicker: false,
  showSkyboxPicker: false,
  showContentPicker: false,
  showAvatarSelector: false,
  showGroundControls: false,
  showContentBrowser: false,
  showSkyboxManager: false,
  showScreenPropPicker: false,
  showBrowserPropPicker: false,
  showAuthPrompt: false,
  showTimeWarning: false,
  showVoiceChatPrompt: false,
  showShareModal: false,
  showAtmosphereControls: false,

  // Avatar
  avatarUrl: null,
  avatarLookTextureUrl: null,
  avatarAnimations: {},
  setAvatarLookTextureUrl: (url: string | null) => set({ avatarLookTextureUrl: url }),
  activeAvatarId: null,
  avatarVisible: true,
  userName: '',
  cameraMode: 'normal',

  // World data
  environmentModels: {},
  skyboxes: {},
  currentSkyboxId: null,
  mobileSkyboxUrl: null,
  contentItems: {},
  screenProps: {},
  primitives: {},
  lights: {},
  atmosphere: {
    fog: 'none',
    fogColor: '#cccccc',
    fogNear: 10,
    fogFar: 100,
    fogDensity: 0.02,
    particles: 'none',
    particleCount: 500,
    particleColor: '#ffffff',
    bloom: false,
    bloomIntensity: 0.5,
    bloomThreshold: 0.8,
    ambientIntensity: 0.4,
    sunIntensity: 1.0,
    sunColor: '#ffffff',
    sunShadows: false,
  },
  spawnPosition: [0, 0, 0] as [number, number, number],
  worldBounds: null,
  worldMusic: null,
  groundVisible: true,
  groundSettings: {
    size: 100,
    color: '#1a1a1a',
    textureUrl: null,
    texturePreset: null,
    textureScale: 10,
    opacity: 1,
    showGrid: true,
    isLocked: false,
  },
  foliageStrokes: [],
  foliageBrush: { radius: 2.5, density: 1.6, species: [0, 3], trees: false, erase: false },

  // Selection
  selectedObjectId: null,
  selectedObjectIds: [],
  isEditingEnvironment: false,

  // Auth & session
  user: null,
  isOwner: true,
  isPublic: true,
  accessCode: null,

  // Save state
  isSaving: false,
  worldLoading: true,
  loadingMessage: 'Initializing...',
  worldName: '',
  lastSaved: null,
  actualWorldId: null,
  saveNotification: { show: false, type: 'success', message: '' },
  shortenedURL: '',
  isLoadingShortURL: false,
  activeScreenShare: null,
  multiplayerInfo: { shardIndex: null, playerCount: 0, socketId: null, connected: false },
  onlinePlayers: [],
  knockRequests: [],
  worldLocked: false,
  vrBuildMode: false,
  vrBuildActive: false,
  localEmote: null,
  activeTool: 'select',
  placementShape: null,
  vrPanelType: null,
  // Build Mode v2: snapping is ON by default so the first click lands on grid.
  snapSettings: { grid: true, gridSize: 0.5, rotation: true, rotationAngle: 15 },
  buildTool: 'none',
  placementSpec: null,
  placementRotY: 0,
  stairSweep: 0,
  topDownView: false,
  paintColor: '#00c2b3',
  editLocks: {},
  lockFlash: null,
  sceneOrder: [],
  remoteObjectIds: new Set<string>(),
  role: 'owner',
  canBuild: true,
  persistenceReady: true,

  // ---- Actions ----

  togglePanel: (panel) =>
    set((s) => ({ [panel]: !(s as any)[panel] } as any)),

  setPanel: (panel, value) =>
    set({ [panel]: value } as any),

  // Avatar
  setAvatarUrl: (url) => {
    set({ avatarUrl: url });
    if (url) localStorage.setItem('eb-avatar-url', url);
    else localStorage.removeItem('eb-avatar-url');
  },
  setAvatarFromDb: (avatar) => {
    set({
      avatarUrl: avatar.model_url,
      avatarLookTextureUrl: avatar.active_texture_url || null,
      avatarAnimations: avatar.animations || {},
      activeAvatarId: avatar.id,
    });
    // Keep localStorage in sync for backward compat
    localStorage.setItem('eb-avatar-url', avatar.model_url);
    localStorage.setItem('eb-avatar-animations', JSON.stringify(avatar.animations || {}));
    // Restore this avatar's saved pose tuning (Avatar Pose sliders)
    const pose = (avatar.animations as any)?.__pose;
    if (pose && typeof pose === 'object') {
      try { localStorage.setItem('gathrxr.poseTune', JSON.stringify(pose)); } catch {}
      if (typeof pose.arm === 'number') (window as any).__armRelax = pose.arm;
      (window as any).__shoulderRelax = { down: pose.down ?? 0.18, forward: pose.forward ?? 0.22 };
    }
  },
  setUserName: (name) => set({ userName: name }),
  setCameraMode: (mode) => set({ cameraMode: mode }),

  // Environment models
  addEnvironmentModel: (id, model) =>
    set((s) => ({ environmentModels: { ...s.environmentModels, [id]: model } })),
  updateEnvironmentModel: (id, updates) =>
    set((s) => ({
      environmentModels: {
        ...s.environmentModels,
        [id]: { ...s.environmentModels[id], ...updates },
      },
    })),
  updateEnvironmentTransform: (id, transform) =>
    set((s) => ({
      environmentModels: {
        ...s.environmentModels,
        [id]: {
          ...s.environmentModels[id],
          transform: { ...s.environmentModels[id].transform, ...transform },
        },
      },
    })),
  deleteEnvironmentModel: (id) =>
    set((s) => {
      const { [id]: _, ...rest } = s.environmentModels;
      return { environmentModels: rest };
    }),

  // Skyboxes
  addSkybox: (id, skybox) =>
    set((s) => ({ skyboxes: { ...s.skyboxes, [id]: skybox } })),
  deleteSkybox: (id) =>
    set((s) => {
      const { [id]: _, ...rest } = s.skyboxes;
      const updates: Partial<WorldState> = { skyboxes: rest };
      if (s.currentSkyboxId === id) updates.currentSkyboxId = null;
      return updates;
    }),
  setCurrentSkyboxId: (id) => set({ currentSkyboxId: id }),
  setMobileSkyboxUrl: (url) => set({ mobileSkyboxUrl: url }),

  // Content items
  addContentItem: (id, item) =>
    set((s) => ({ contentItems: { ...s.contentItems, [id]: item } })),
  updateContentItem: (id, updates) =>
    set((s) => ({
      contentItems: {
        ...s.contentItems,
        [id]: { ...s.contentItems[id], ...updates },
      },
    })),
  deleteContentItem: (id) =>
    set((s) => {
      const { [id]: _, ...rest } = s.contentItems;
      return { contentItems: rest };
    }),

  // Screen props
  addScreenProp: (id, prop) =>
    set((s) => ({ screenProps: { ...s.screenProps, [id]: prop } })),
  updateScreenProp: (id, updates) =>
    set((s) => ({
      screenProps: {
        ...s.screenProps,
        [id]: { ...s.screenProps[id], ...updates },
      },
    })),
  deleteScreenProp: (id) =>
    set((s) => {
      const { [id]: _, ...rest } = s.screenProps;
      return { screenProps: rest };
    }),

  // Primitives
  addPrimitive: (id, item) =>
    set((s) => ({ primitives: { ...s.primitives, [id]: item } })),
  updatePrimitive: (id, updates) =>
    set((s) => ({
      primitives: {
        ...s.primitives,
        [id]: { ...s.primitives[id], ...updates },
      },
    })),
  deletePrimitive: (id) =>
    set((s) => {
      const { [id]: _, ...rest } = s.primitives;
      return { primitives: rest };
    }),

  // Lights
  addLight: (id, item) =>
    set((s) => ({ lights: { ...s.lights, [id]: item } })),
  updateLight: (id, updates) =>
    set((s) => ({
      lights: {
        ...s.lights,
        [id]: { ...s.lights[id], ...updates },
      },
    })),
  deleteLight: (id) =>
    set((s) => {
      const { [id]: _, ...rest } = s.lights;
      return { lights: rest };
    }),

  // Ground
  setGroundSettings: (settings) =>
    set((s) => ({ groundSettings: { ...s.groundSettings, ...settings } })),
  addFoliageStroke: (stroke) => set((s) => ({ foliageStrokes: [...s.foliageStrokes, stroke] })),
  undoFoliageStroke: () => set((s) => ({ foliageStrokes: s.foliageStrokes.slice(0, -1) })),
  clearFoliageStrokes: () => set({ foliageStrokes: [] }),
  setFoliageStrokes: (strokes) => set({ foliageStrokes: strokes }),
  setFoliageBrush: (b) => set((s) => ({ foliageBrush: { ...s.foliageBrush, ...b } })),
  setGroundVisible: (visible) => set({ groundVisible: visible }),

  // Atmosphere
  setAtmosphere: (settings) =>
    set((s) => ({ atmosphere: { ...s.atmosphere, ...settings } })),

  // Spawn
  setSpawnPosition: (pos) => set({ spawnPosition: pos }),
  setWorldBounds: (bounds) => set({ worldBounds: bounds }),
  setWorldMusic: (music) => set({ worldMusic: music }),

  // Multiplayer debug
  setMultiplayerInfo: (info) =>
    set((s) => ({ multiplayerInfo: { ...s.multiplayerInfo, ...info } })),
  setOnlinePlayers: (players) => set({ onlinePlayers: players }),
  // Blocked (either way) people can't knock on my world.
  addKnockRequest: (req) => set((s) => (isHiddenUser(req.userId) ? {} : { knockRequests: [...s.knockRequests.filter(k => k.userId !== req.userId), req] })),
  removeKnockRequest: (userId) => set((s) => ({ knockRequests: s.knockRequests.filter(k => k.userId !== userId) })),
  setWorldLocked: (locked) => set({ worldLocked: locked }),
  setVrBuildMode: (active) => set({ vrBuildMode: active }),
  setVrBuildActive: (active) => set({ vrBuildActive: active }),

  // Emotes (ephemeral, local player only)
  playLocalEmote: (name) => set((s) => ({ localEmote: { name, nonce: (s.localEmote?.nonce ?? 0) + 1 } })),
  stopLocalEmote: () => set({ localEmote: null }),

  // In-world building tools
  setActiveTool: (tool) => set({ activeTool: tool }),
  setPlacementShape: (shape) => set({ placementShape: shape, vrPanelType: null }),
  setVrPanelType: (vrPanelType) => set({ vrPanelType, placementShape: null }),
  setSnapSettings: (settings) => set((s) => ({ snapSettings: { ...s.snapSettings, ...settings } })),

  // Build Mode v2. Leaving 'place' always drops whatever the ghost was holding
  // so a stale spec can never resurrect on the next tool switch.
  setBuildTool: (tool) => set((s) => ({
    buildTool: tool,
    placementSpec: tool === 'place' ? s.placementSpec : null,
    placementRotY: tool === 'place' ? s.placementRotY : 0,
  })),
  setPlacementSpec: (spec) => set({ placementSpec: spec }),
  setPlacementRotY: (rot) => set({ placementRotY: rot }),
  setStairSweep: (deg) => set({ stairSweep: deg }),
  setTopDownView: (on) => set({ topDownView: on }),
  setPaintColor: (color) => set({ paintColor: color }),

  setEditLock: (objectId, lock) => set((s) => {
    const editLocks = { ...s.editLocks };
    if (lock) editLocks[objectId] = { ...lock, at: Date.now() };
    else delete editLocks[objectId];
    return { editLocks };
  }),

  flashLocked: (objectId) => set({ lockFlash: { id: objectId, at: Date.now() } }),
  // Scene order
  setSceneOrder: (order) => set({ sceneOrder: order }),

  // Remote object tracking
  addRemoteObjectId: (id) =>
    set((s) => { const next = new Set(s.remoteObjectIds); next.add(id); return { remoteObjectIds: next }; }),
  removeRemoteObjectId: (id) =>
    set((s) => { const next = new Set(s.remoteObjectIds); next.delete(id); return { remoteObjectIds: next }; }),

  // Role
  setRole: (role) => set({ role, canBuild: role === 'owner' || role === 'host' || role === 'builder' }),
  setPersistenceReady: (persistenceReady) => set({ persistenceReady }),

  // Selection
  setSelectedObjectId: (id) => set({ selectedObjectId: id }),
  toggleMultiSelect: (id) => set((state) => {
    const ids = state.selectedObjectIds.includes(id)
      ? state.selectedObjectIds.filter((x) => x !== id)
      : [...state.selectedObjectIds, id];
    return { selectedObjectIds: ids };
  }),
  setMultiSelect: (ids) => set({ selectedObjectIds: ids }),
  clearMultiSelect: () => set({ selectedObjectIds: [] }),
  setIsEditingEnvironment: (editing) => set({ isEditingEnvironment: editing }),

  // Grouping
  groupSelected: () => set((state) => {
    // Groups span shapes AND content models.
    const primIds = state.selectedObjectIds.filter((id) => id.startsWith('prim-') && state.primitives[id]);
    const contentIds = state.selectedObjectIds.filter((id) => id.startsWith('content-') && state.contentItems[id]);
    if (primIds.length + contentIds.length < 2) return state;
    const groupId = `group-${Date.now()}`;
    const primitives = { ...state.primitives };
    for (const id of primIds) primitives[id] = { ...primitives[id], groupId };
    const contentItems = { ...state.contentItems };
    for (const id of contentIds) contentItems[id] = { ...contentItems[id], groupId };
    return { primitives, contentItems, selectedObjectIds: [] };
  }),
  ungroupSelected: () => set((state) => {
    const id = state.selectedObjectId;
    const gid = (id && (state.primitives[id]?.groupId || state.contentItems[id]?.groupId)) || null;
    if (!gid) return state;
    const primitives = { ...state.primitives };
    for (const [pid, prim] of Object.entries(primitives)) {
      if (prim.groupId === gid) primitives[pid] = { ...prim, groupId: undefined };
    }
    const contentItems = { ...state.contentItems };
    for (const [cid, item] of Object.entries(contentItems)) {
      if (item.groupId === gid) contentItems[cid] = { ...item, groupId: undefined };
    }
    return { primitives, contentItems };
  }),
  getGroupMembers: (groupId) => {
    const state = useWorldStore.getState();
    return [
      ...Object.entries(state.primitives).filter(([, p]) => p.groupId === groupId).map(([id]) => id),
      ...Object.entries(state.contentItems).filter(([, c]) => c.groupId === groupId).map(([id]) => id),
    ];
  },

  // Auth
  setUser: (user) => set({ user }),
  setIsOwner: (isOwner) => set({ isOwner }),
  setIsPublic: (isPublic) => set({ isPublic }),
  setAccessCode: (code) => set({ accessCode: code }),
  generateAccessCode: () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    set({ accessCode: code });
    return code;
  },

  // Save
  setIsSaving: (saving) => set({ isSaving: saving }),
  setLastSaved: (date) => set({ lastSaved: date }),
  setActualWorldId: (id) => set({ actualWorldId: id }),
  setSaveNotification: (notification) => set({ saveNotification: notification }),
  setActiveScreenShare: (stream) => set({ activeScreenShare: stream }),
  setShortenedURL: (url) => set({ shortenedURL: url }),
  setIsLoadingShortURL: (loading) => set({ isLoadingShortURL: loading }),
  setWorldLoading: (loading) => set({ worldLoading: loading }),
  setLoadingMessage: (msg) => set({ loadingMessage: msg }),
  setWorldName: (name) => set({ worldName: name }),

  // Bulk loaders
  setSkyboxes: (skyboxes) => set({ skyboxes }),
  setEnvironmentModels: (models) => set({ environmentModels: models }),
  setContentItems: (items) => set({ contentItems: items }),

  resetWorldData: () => set({
    vrPanelType: null,
    placementShape: null,
    environmentModels: {},
    skyboxes: {},
    currentSkyboxId: null,
    mobileSkyboxUrl: null,
    contentItems: {},
    screenProps: {},
    primitives: {},
    lights: {},
    selectedObjectId: null,
    selectedObjectIds: [],
    isEditingEnvironment: false,
    spawnPosition: [0, 0, 0] as [number, number, number],
    worldBounds: null,
    worldMusic: null,
    sceneOrder: [],
    remoteObjectIds: new Set<string>(),
    lastSaved: null,
    saveNotification: { show: false, type: 'success', message: '' },
  }),

  loadWorldAssets: (assets) => {
    const newEnvironments: Record<string, EnvironmentModel> = {};
    const newSkyboxes: Record<string, SkyboxEntry> = {};
    const newContentItems: Record<string, ContentItem> = {};
    const newPrimitives: Record<string, PrimitiveItem> = {};
    const newLights: Record<string, LightItem> = {};
    const newScreenProps: Record<string, ScreenPropItem> = {};
    let currentSkybox: string | null = null;

    // Stable id from the row (write-through or an id-stamping save); falls
    // back to index keys for legacy rows. Stable ids keep every client keying
    // the same object identically — required for live sync and write-through.
    const stableId = (asset: any, fallback: string): string =>
      asset.client_id || asset.metadata?.id || fallback;
    // Write-through only when every syncable object has a stable id
    const persistenceReady = assets.every((a: any) =>
      !['primitive', 'light', 'content', 'environment', 'skybox'].includes(a.type) || a.client_id || a.metadata?.id
    );

    assets.forEach((asset: any, index: number) => {
      if (asset.type === 'environment') {
        newEnvironments[stableId(asset, `env-${index}`)] = {
          url: asset.url,
          transform: {
            position: asset.metadata?.position || [0, 0, 0],
            scale: asset.metadata?.scale || 1,
            rotation: asset.metadata?.rotation || [0, 0, 0],
            isLocked: asset.metadata?.isLocked || false,
          },
          visible: asset.metadata?.visible ?? true,
          collision: asset.metadata?.collision === 'none' ? 'none' : 'auto',
        };
      } else if (asset.type === 'skybox') {
        const skyboxId = stableId(asset, `skybox-${index}`);
        newSkyboxes[skyboxId] = {
          url: asset.url,
          filename: asset.metadata?.filename || 'Skybox',
          uploadedAt: asset.metadata?.uploadedAt || Date.now(),
          thumbnail: asset.metadata?.thumbnail,
        };
        if (asset.metadata?.isCurrent) currentSkybox = skyboxId;
      } else if (asset.type === 'content') {
        newContentItems[stableId(asset, `content-${index}`)] = {
          url: asset.url,
          type: asset.metadata?.contentType as 'image' | 'audio' | 'model' | 'splat' | 'ply',
          position: asset.metadata?.position || [0, 0, 0],
          scale: asset.metadata?.scale || 1,
          rotation: asset.metadata?.rotation || [0, 0, 0],
          visible: asset.metadata?.visible ?? true,
          isLocked: asset.metadata?.isLocked ?? false,
          collidable: asset.metadata?.collidable ?? false,
          seat: asset.metadata?.seat ?? false,
          environmentMesh: asset.metadata?.environmentMesh ?? false,
          mobileUrl: asset.metadata?.mobileUrl || undefined,
          materialColors: asset.metadata?.materialColors || undefined,
          materialTextures: asset.metadata?.materialTextures || undefined,
          groupId: asset.metadata?.groupId ?? undefined,
          filename: asset.metadata?.filename,
          thumbnailUrl: asset.metadata?.thumbnailUrl,
          uploadedAt: asset.metadata?.uploadedAt,
        };
      } else if (asset.type === 'primitive') {
        newPrimitives[stableId(asset, `prim-${index}`)] = {
          shape: asset.metadata?.shape || 'box',
          name: asset.metadata?.name || 'Primitive',
          position: asset.metadata?.position || [0, 0.5, 0],
          rotation: asset.metadata?.rotation || [0, 0, 0],
          scale: asset.metadata?.scale || [1, 1, 1],
          color: asset.metadata?.color || '#888888',
          texturePreset: asset.metadata?.texturePreset || null,
          textureUrl: asset.metadata?.textureUrl || null,
          pbrMaps: asset.metadata?.pbrMaps || null,
          textureScale: asset.metadata?.textureScale || 2,
          textureScaleY: asset.metadata?.textureScaleY ?? undefined,
          visible: asset.metadata?.visible ?? true,
          isLocked: asset.metadata?.isLocked ?? false,
          collidable: asset.metadata?.collidable ?? false,
          opacity: asset.metadata?.opacity ?? 1,
          emissive: asset.metadata?.emissive ?? 0,
          seat: asset.metadata?.seat ?? false,
          seatFit: asset.metadata?.seatFit === 'chair' || asset.metadata?.seatFit === 'deep' ? asset.metadata.seatFit : undefined,
          portalLink: asset.metadata?.portalLink ?? undefined,
          portalLabel: asset.metadata?.portalLabel ?? undefined,
          portalWorldId: asset.metadata?.portalWorldId ?? undefined,
          portalWorldName: asset.metadata?.portalWorldName ?? undefined,
          game: asset.metadata?.game ?? undefined,
          gameStyle: asset.metadata?.gameStyle ?? undefined,
          arcadeGame: typeof asset.metadata?.arcadeGame === 'string' ? asset.metadata.arcadeGame : undefined,
          board: asset.metadata?.board && typeof asset.metadata.board === 'object' ? asset.metadata.board : undefined,
          npc: asset.metadata?.npc ?? undefined,
          speaker: asset.metadata?.shape === 'speaker' ? parseSpeaker(asset.metadata?.speaker) : undefined,
          worldGame: asset.metadata?.shape === 'worldgame' ? parseWorldGame(asset.metadata?.worldGame) : undefined,
          groupId: asset.metadata?.groupId ?? undefined,
          metalness: asset.metadata?.metalness ?? 0,
          roughness: asset.metadata?.roughness ?? 0.5,
          colliderOnly: asset.metadata?.colliderOnly ?? false,
          castShadow: asset.metadata?.castShadow === false ? false : undefined,
          text: asset.metadata?.text,
          fontSize: asset.metadata?.fontSize,
        };
      } else if (asset.type === 'screenprop') {
        // A saved share panel waits for the room’s next live presentation.
        newScreenProps[stableId(asset, `screen-${index}`)] = {
          type: asset.metadata?.screenType === 'screen-share' ? 'screen-share' : asset.metadata?.screenType === 'video' ? 'video' : 'browser',
          position: asset.metadata?.position || [0, 2, -5],
          scale: asset.metadata?.scale || 1,
          rotation: asset.metadata?.rotation || [0, 0, 0],
          visible: asset.metadata?.visible ?? true,
          isLocked: asset.metadata?.isLocked ?? false,
          browserUrl: asset.metadata?.browserUrl || undefined,
          videoUrl: asset.metadata?.videoUrl || undefined,
          filename: asset.metadata?.filename,
          uploadedAt: asset.metadata?.uploadedAt,
          roomScreen: asset.metadata?.roomScreen === true || undefined,
          width: typeof asset.metadata?.width === 'number' ? asset.metadata.width : undefined,
          height: typeof asset.metadata?.height === 'number' ? asset.metadata.height : undefined,
          name: asset.metadata?.name || undefined,
        };
      } else if (asset.type === 'light') {
        newLights[stableId(asset, `light-${index}`)] = {
          type: asset.metadata?.lightType || 'spotlight',
          name: asset.metadata?.name || 'Light',
          position: asset.metadata?.position || [0, 5, 0],
          rotation: asset.metadata?.rotation || [-Math.PI / 2, 0, 0],
          color: asset.metadata?.color || '#ffffff',
          intensity: asset.metadata?.intensity ?? 1,
          distance: asset.metadata?.distance ?? 20,
          angle: asset.metadata?.angle ?? 0.5,
          penumbra: asset.metadata?.penumbra ?? 0.5,
          castShadow: asset.metadata?.castShadow ?? true,
          visible: asset.metadata?.visible ?? true,
          isLocked: asset.metadata?.isLocked ?? false,
          width: asset.metadata?.width ?? 4,
          height: asset.metadata?.height ?? 4,
        };
      }
    });

    set({
      environmentModels: newEnvironments,
      skyboxes: newSkyboxes,
      contentItems: newContentItems,
      primitives: newPrimitives,
      lights: newLights,
      screenProps: newScreenProps,
      currentSkyboxId: currentSkybox,
      persistenceReady,
    });
  },
}), {
  // Only track changes to world data fields (not UI panels, selection, auth, etc.)
  partialize: (state) => {
    const partial: Record<string, any> = {};
    for (const key of TRACKED_FIELDS) {
      partial[key] = (state as any)[key];
    }
    return partial;
  },
  limit: 50, // Keep last 50 states
  // A set that changes no world data (selection, panels, presence…) is not a step.
  equality: (a, b) => {
    for (const key of TRACKED_FIELDS) if ((a as any)[key] !== (b as any)[key]) return false;
    return true;
  },
  // One step per burst: record the state from BEFORE the first change, and
  // fold changes that follow within UNDO_MERGE_MS of each other (a drag, a
  // slider) into it. (The old trailing debounce kept the state before the
  // LAST change, so undo after an add or a drag often did nothing.)
  handleSet: (handleSet) => {
    let last = 0;
    return (pastState, replace) => {
      const now = Date.now();
      if (now - last > UNDO_MERGE_MS) handleSet(pastState, replace);
      last = now;
    };
  },
}));

export { DEFAULT_AVATAR_URL };
